import React, { createContext, useState, useContext, useEffect } from 'react';
import api, { authAPI } from '../services/api';
import supabase, { isSupabaseAuthEnabled } from '../services/supabase';
import { isNative, platform as nativePlatform, initializePushNotifications } from '../services/capacitor';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isSupabaseAuthEnabled()) {
      // Supabase auth: listen for session changes
      const initSupabaseAuth = async () => {
        // If masquerading, restore from localStorage instead of Supabase session.
        // Belt-and-braces: a lingering isMasquerading flag with no originalAccessToken
        // means a prior masquerade was torn down without clearing the flag (or a fresh
        // login didn't clear it). Treat that as a stuck flag — wipe the masquerade keys
        // and fall through to the real Supabase session path.
        if (localStorage.getItem('isMasquerading') === 'true') {
          if (localStorage.getItem('originalAccessToken')) {
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
              setUser(JSON.parse(storedUser));
            }
            setLoading(false);
            return;
          }
          localStorage.removeItem('isMasquerading');
          localStorage.removeItem('originalRefreshToken');
          localStorage.removeItem('originalUser');
          localStorage.removeItem('originalSupabaseAuth');
        }

        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const meta = session.user.user_metadata;
          const userData = {
            id: meta.legacy_id,
            email: session.user.email,
            firstName: meta.first_name,
            lastName: meta.last_name,
            role: meta.user_role,
            tenantId: meta.tenant_id,
            resellerId: meta.reseller_id,
            tenantName: ''
          };
          setUser(userData);
          // Store for API interceptor
          localStorage.setItem('accessToken', session.access_token);
          localStorage.setItem('user', JSON.stringify(userData));
        }
        setLoading(false);
      };

      initSupabaseAuth();

      // Listen for auth state changes (auto token refresh handled by Supabase)
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
            if (session && localStorage.getItem('isMasquerading') !== 'true') {
              localStorage.setItem('accessToken', session.access_token);
              const meta = session.user.user_metadata;
              const userData = {
                id: meta.legacy_id,
                email: session.user.email,
                firstName: meta.first_name,
                lastName: meta.last_name,
                role: meta.user_role,
                tenantId: meta.tenant_id,
                resellerId: meta.reseller_id,
                tenantName: ''
              };
              localStorage.setItem('user', JSON.stringify(userData));
              setUser(userData);
            }
          } else if (event === 'SIGNED_OUT') {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('user');
            setUser(null);
          }
        }
      );

      return () => subscription.unsubscribe();
    } else {
      // Legacy auth: check localStorage
      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');
      const userData = localStorage.getItem('user');

      if (accessToken && refreshToken && userData) {
        try {
          setUser(JSON.parse(userData));
        } catch (error) {
          console.error('Failed to parse user data:', error);
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
        }
      }

      setLoading(false);
    }
  }, []);

  // Register for native push on first authenticated render. Web is a no-op,
  // and the token callback is idempotent on the backend so re-runs are safe.
  useEffect(() => {
    if (!user || !isNative) return;
    let cancelled = false;
    (async () => {
      await initializePushNotifications(async (deviceToken) => {
        if (cancelled) return;
        try {
          // Stable per-install UUID so the backend can dedupe prior
          // tokens for THIS install when APNs/FCM rotates the device
          // token (reinstall, OS update). Without this, every rotation
          // leaves a zombie row that APNs still 200s but never delivers.
          let installationId = localStorage.getItem('pushInstallationId');
          if (!installationId) {
            installationId = (window.crypto?.randomUUID?.() ||
              `${Date.now()}-${Math.random().toString(36).slice(2)}`);
            localStorage.setItem('pushInstallationId', installationId);
          }
          localStorage.setItem('pushDeviceToken', deviceToken);

          // Pull a human-ish device label from Capacitor's Device plugin
          // so the Push Registrations admin view can tell rows apart.
          // iOS post-16 doesn't return the user-set name without a
          // private entitlement, so we fall back to model + OS — still
          // enough to distinguish "iPhone15,3 / iOS 17.2" from
          // "Pixel 8 / Android 14".
          let deviceName = `${nativePlatform} device`;
          try {
            const { Device } = await import('@capacitor/device');
            const info = await Device.getInfo();
            const modelOrName = info.name && info.name !== info.model ? info.name : info.model;
            const os = [info.operatingSystem, info.osVersion].filter(Boolean).join(' ');
            deviceName = [modelOrName, os].filter(Boolean).join(' / ') || deviceName;
          } catch (e) { /* plugin missing or call failed — keep fallback */ }

          // Prefer Capacitor's real native app version on device — it's
          // set from CFBundleShortVersionString on iOS and versionName on
          // Android at build time by Xcode/Gradle, so it always matches
          // the installed IPA/AAB. Codemagic doesn't inject
          // REACT_APP_VERSION into the CRA build, so the process.env
          // fallback resolves to '1.0.0' for every native install (why
          // every push_tokens row was stamped 1.0.0). Using the real
          // native version fixes that without needing to sync codemagic
          // env vars with MARKETING_VERSION in pbxproj.
          let appVersion = process.env.REACT_APP_VERSION || '1.0.0';
          try {
            const { App } = await import('@capacitor/app');
            const info = await App.getInfo();
            if (info?.version) appVersion = info.version;
          } catch (e) { /* plugin missing or call failed — keep fallback */ }

          await api.post('/push/register', {
            deviceToken,
            platform: nativePlatform,
            deviceName,
            appVersion,
            installationId
          });
        } catch (err) {
          console.warn('[Push] backend register failed:', err?.response?.data?.error || err.message);
        }
      });
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // Proactive token refresh - only for legacy auth (Supabase handles its own)
  useEffect(() => {
    if (!user || isSupabaseAuthEnabled()) return;

    const refreshTokenProactively = async () => {
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) return;

        console.log('Proactively refreshing token...');
        const response = await authAPI.refresh(refreshToken);

        if (response.data.accessToken) {
          localStorage.setItem('accessToken', response.data.accessToken);
          console.log('Token refreshed successfully');
        }
      } catch (error) {
        console.error('Proactive token refresh failed:', error);
      }
    };

    // Refresh token every 50 minutes (3000000ms)
    const refreshInterval = setInterval(refreshTokenProactively, 50 * 60 * 1000);

    // Also do an immediate refresh if token is close to expiring
    const immediateRefreshTimeout = setTimeout(refreshTokenProactively, 1000);

    return () => {
      clearInterval(refreshInterval);
      clearTimeout(immediateRefreshTimeout);
    };
  }, [user]);

  const login = async (credentials) => {
    try {
      // Emails are stored lowercase in both public.users and auth.users;
      // GoTrue's signInWithPassword used to do its own case-fold but
      // stopped in some v2.x versions, so a user typing "Bret@…" would
      // clear the preflight (which does .normalizeEmail()) and then fail
      // the actual Supabase sign-in. Normalize once, use everywhere.
      const normalizedEmail = (credentials.email || '').trim().toLowerCase();
      credentials = { ...credentials, email: normalizedEmail };

      if (isSupabaseAuthEnabled()) {
        // Preflight check BEFORE minting a Supabase session. This
        // verifies password + 2FA state against our DB without touching
        // Supabase at all — so a 2FA-required user never has a live
        // session that could leak app access in the race window
        // between signInWithPassword and the follow-up check.
        //
        // FAIL CLOSED on any preflight error: an auth control must not
        // silently bypass when the backend hiccups. Better UX to show
        // a "try again" than to grant access.
        let pre;
        try {
          const emailKey = (credentials.email || '').toLowerCase().trim();
          const trustToken = emailKey ? localStorage.getItem(`trustedDeviceToken:${emailKey}`) : null;
          pre = await api.post('/auth/preflight-login', {
            email: credentials.email,
            password: credentials.password,
            ...(trustToken ? { trustToken } : {}),
          });
          // If the backend rejected a stale/revoked token (requires2FA
          // comes back true), drop the local copy so we don't keep
          // sending it every attempt.
          if (trustToken && pre.data?.requires2FA) {
            localStorage.removeItem(`trustedDeviceToken:${emailKey}`);
          }
        } catch (e) {
          const status = e?.response?.status;
          const body = e?.response?.data;
          if (status === 401) return { success: false, error: body?.error || 'Invalid credentials', remainingAttempts: body?.remainingAttempts };
          if (status === 403) return { success: false, error: body?.error || 'Access denied' };
          if (status === 429) return { success: false, error: body?.error || 'Too many attempts. Please try again later.' };
          // Every other error (500, network, timeout, CORS) fails closed.
          return { success: false, error: 'Unable to verify sign-in. Please try again.' };
        }
        if (pre.data?.requires2FA) {
          // Password OK, code emailed by preflight. Never touched
          // Supabase — no session to sign out. Route to /verify-2fa.
          return {
            success: false,
            requires2FA: true,
            userId: pre.data.userId,
            email: pre.data.email,
            password: credentials.password,
          };
        }

        // Preflight cleared. Only NOW establish the Supabase session.
        const { data, error } = await supabase.auth.signInWithPassword({
          email: credentials.email,
          password: credentials.password
        });

        if (error) {
          return { success: false, error: error.message };
        }

        const meta = data.user.user_metadata;
        const userData = {
          id: meta.legacy_id,
          email: data.user.email,
          firstName: meta.first_name,
          lastName: meta.last_name,
          role: meta.user_role,
          tenantId: meta.tenant_id,
          resellerId: meta.reseller_id,
          tenantName: ''
        };

        localStorage.removeItem('isMasquerading');
        localStorage.removeItem('originalAccessToken');
        localStorage.removeItem('originalRefreshToken');
        localStorage.removeItem('originalUser');
        localStorage.removeItem('originalSupabaseAuth');

        localStorage.setItem('accessToken', data.session.access_token);
        localStorage.setItem('user', JSON.stringify(userData));

        window.location.href = '/';
        return { success: true };
      }

      // Legacy auth login
      const response = await authAPI.login(credentials);

      // Check if 2FA is required
      if (response.data.requires2FA) {
        return {
          success: false,
          requires2FA: true,
          userId: response.data.userId,
          email: response.data.email,
          message: response.data.message
        };
      }

      // Normal login flow
      const { accessToken, refreshToken, user: userData } = response.data;

      // Clear all old auth data first to prevent caching issues
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('isMasquerading');
      localStorage.removeItem('originalAccessToken');
      localStorage.removeItem('originalRefreshToken');
      localStorage.removeItem('originalUser');
      localStorage.removeItem('originalSupabaseAuth');

      // Store new tokens
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(userData));

      console.log('Login successful, tokens saved. Reloading...');

      // Force a hard reload to bypass service worker cache
      window.location.href = '/';

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed',
        remainingAttempts: error.response?.data?.remainingAttempts
      };
    }
  };

  const register = async (data) => {
    try {
      // Registration always goes through Express (creates tenant + user)
      const response = await authAPI.register(data);
      const { accessToken, refreshToken, user: userData } = response.data;

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed'
      };
    }
  };

  const logout = async () => {
    // Unregister the push token for THIS device first so the user
    // stops receiving notifications immediately after sign-out.
    // installationId stays in localStorage — it identifies the device,
    // not the session, so the next sign-in reuses it.
    try {
      const deviceToken = localStorage.getItem('pushDeviceToken');
      if (deviceToken) {
        await api.post('/push/unregister', { deviceToken });
      }
    } catch (e) {
      // Non-fatal — proceed with sign-out even if unregister fails.
      console.warn('[Push] unregister on logout failed:', e?.response?.data?.error || e.message);
    }
    localStorage.removeItem('pushDeviceToken');

    try {
      if (isSupabaseAuthEnabled()) {
        await supabase.auth.signOut();
      } else {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          await authAPI.logout(refreshToken);
        }
      }
    } catch (error) {
      console.error('Logout API error:', error);
    }

    // Clear all tokens and user data
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('originalAccessToken');
    localStorage.removeItem('originalRefreshToken');
    localStorage.removeItem('originalUser');
    localStorage.removeItem('isMasquerading');
    setUser(null);
    window.location.href = '/login';
  };

  const masquerade = async (userId) => {
    try {
      const response = await authAPI.masquerade(userId);
      const { accessToken, refreshToken, supabaseAccessToken, user: masqueradeUser } = response.data;

      // Store original tokens and user before masquerading
      const currentAccessToken = localStorage.getItem('accessToken');
      const currentRefreshToken = localStorage.getItem('refreshToken');
      const currentUser = localStorage.getItem('user');
      const currentSupabaseAuth = localStorage.getItem('supabase-auth');
      localStorage.setItem('originalAccessToken', currentAccessToken);
      localStorage.setItem('originalRefreshToken', currentRefreshToken);
      localStorage.setItem('originalUser', currentUser);
      if (currentSupabaseAuth) {
        localStorage.setItem('originalSupabaseAuth', currentSupabaseAuth);
      }

      // Swap the Supabase session to the target user. Without this, the
      // admin's original Supabase session keeps making PostgREST,
      // Realtime, and Storage requests under super_admin RLS → every
      // Supabase-backed page (Contacts, Tags, Templates, Lists, Blocked
      // Senders, Realtime, Storage) leaks cross-tenant data during
      // masquerade. If the backend couldn't mint a target Supabase JWT
      // (e.g. no auth.users row), sign out locally so those pages 401
      // rather than return the admin's data.
      if (isSupabaseAuthEnabled() && supabase) {
        try {
          if (supabaseAccessToken) {
            await supabase.auth.setSession({
              access_token: supabaseAccessToken,
              refresh_token: 'masquerade-no-refresh', // intentionally non-refreshable; session ends with JWT expiry
            });
          } else {
            await supabase.auth.signOut({ scope: 'local' });
          }
        } catch (supErr) {
          console.warn('[masquerade] Supabase session swap failed; signing out locally to be safe:', supErr.message);
          try { await supabase.auth.signOut({ scope: 'local' }); } catch { /* swallow */ }
        }
      }

      // Set masquerade tokens and user
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(masqueradeUser));
      localStorage.setItem('isMasquerading', 'true');
      setUser(masqueradeUser);

      // Reload to refresh all data
      window.location.href = '/';

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Masquerade failed'
      };
    }
  };

  const stopMasquerading = () => {
    const originalAccessToken = localStorage.getItem('originalAccessToken');
    const originalRefreshToken = localStorage.getItem('originalRefreshToken');
    const originalUser = localStorage.getItem('originalUser');
    const originalSupabaseAuth = localStorage.getItem('originalSupabaseAuth');

    if (originalAccessToken && originalRefreshToken && originalUser) {
      localStorage.setItem('accessToken', originalAccessToken);
      localStorage.setItem('refreshToken', originalRefreshToken);
      localStorage.setItem('user', originalUser);
      localStorage.removeItem('originalAccessToken');
      localStorage.removeItem('originalRefreshToken');
      localStorage.removeItem('originalUser');
      localStorage.removeItem('isMasquerading');

      // Restore the admin's original Supabase session so PostgREST /
      // Realtime / Storage stop firing as the masquerade target. The
      // page reload below re-initializes the supabase client which
      // reads this key on construction.
      if (originalSupabaseAuth) {
        localStorage.setItem('supabase-auth', originalSupabaseAuth);
        localStorage.removeItem('originalSupabaseAuth');
      } else {
        // No saved Supabase session (admin wasn't logged in to Supabase
        // at masquerade time). Clear whatever masquerade session is in
        // place so we don't carry the target's identity back out.
        localStorage.removeItem('supabase-auth');
      }

      try {
        setUser(JSON.parse(originalUser));
      } catch (error) {
        console.error('Failed to parse original user data:', error);
      }

      // Reload to refresh all data
      window.location.href = '/';
    }
  };

  const value = {
    user,
    setUser,
    login,
    register,
    logout,
    masquerade,
    stopMasquerading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'reseller_admin',
    isSuperAdmin: user?.role === 'super_admin',
    isResellerAdmin: user?.role === 'reseller_admin',
    isMasquerading: localStorage.getItem('isMasquerading') === 'true',
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
